import React from "react";
import {SettingOutlined} from "@ant-design/icons";
import {Input, InputSizes, Select, SelectSizes, Switch, SwitchSizes, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import ComposerSection from "./ComposerSection";
import {SYNC_STATUS_OPTIONS} from "../uploads-simulator.constants";
import styles from "../styles/sor-composer.module.scss";

const BOOLEAN_SETTINGS = [
    // The engine's `_isManualFlowOrigin` only honors boolean true / "true" —
    // a Switch, never free text (a string value would silently clobber the
    // manual_batch mode default and kill the batch trigger).
    {key: "manual_flow_origin", label: "Manual flow origin"},
    {key: "onboarding_complete", label: "Onboarding complete"},
    {key: "verified", label: "Verified"},
    {key: "salary_exempt", label: "Salary exempt"},
    {key: "social_security_administration", label: "SSA member"},
    {key: "supports_time_and_attendance", label: "Supports time & attendance"}
];

/**
 * SOR account-settings section. Only the service-whitelisted `settings` keys
 * are editable; `sync_status` / `no_sync` shape the pre-existing sync row the
 * simulation sees.
 *
 * @param {Object} props
 * @param {Object} props.settings — `sor.settings` (whitelisted keys only)
 * @param {string | null} props.syncStatus — `sor.sync_status`
 * @param {boolean} props.noSync — `sor.no_sync`
 * @param {boolean} [props.filled]
 * @param {(settings: Object) => void} props.onSettingsChange
 * @param {(syncStatus: string | null) => void} props.onSyncStatusChange
 * @param {(noSync: boolean) => void} props.onNoSyncChange
 */
function SorAccountSettingsSection({settings, syncStatus, noSync, filled, onSettingsChange, onSyncStatusChange, onNoSyncChange}) {
    const value = settings || {};
    const setSetting = (key, settingValue) => onSettingsChange({...value, [key]: settingValue});

    return (
        <ComposerSection icon={<SettingOutlined />} title="Account Settings & Sync" filled={filled} defaultCollapsed>
            <div className={styles.toggleGrid}>
                {BOOLEAN_SETTINGS.map(({key, label}) => (
                    <div key={key} className={styles.toggleRow}>
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.toggleLabel}>
                            {label}
                        </Text>
                        <Switch size={SwitchSizes.SMALL} checked={!!value[key]} onChange={checked => setSetting(key, checked)} />
                    </div>
                ))}
            </div>

            <div className={styles.fieldGrid}>
                <Input label="Member id" size={InputSizes.MEDIUM} value={value.member_id || ""} onChange={event => setSetting("member_id", event.target.value)} />
                <Input label="Linked account id" size={InputSizes.MEDIUM} value={value.linked_account_id || ""} onChange={event => setSetting("linked_account_id", event.target.value)} />
                <Input label="Worker type" size={InputSizes.MEDIUM} placeholder="e.g. hourly" value={value.type || ""} onChange={event => setSetting("type", event.target.value)} />
            </div>

            <div className={styles.fieldGrid}>
                <Select
                    label="Existing sync status"
                    size={SelectSizes.MEDIUM}
                    allowClear
                    placeholder="None (default)"
                    disabled={noSync}
                    options={SYNC_STATUS_OPTIONS}
                    value={syncStatus || undefined}
                    onChange={status => onSyncStatusChange(status || null)}
                />
                <div className={styles.toggleRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.toggleLabel}>
                        No sync row (simulate missing sync)
                    </Text>
                    <Switch size={SwitchSizes.SMALL} checked={!!noSync} onChange={onNoSyncChange} />
                </div>
            </div>
        </ComposerSection>
    );
}

export default SorAccountSettingsSection;
